const SPLINE_LIKE_PROFILE_CURVE_KINDS = new Set<string>([
  'spline',
  'bspline',
  'bezier',
]);

const SPLINE_LIKE_EXTRUDE_SOURCE_TYPES = new Set<string>([
  'spline_geometry',
  'bezier_geometry',
]);

const CURVE_PROFILE_SEGMENT_KINDS = new Set<string>([
  'circle',
  'ellipse',
  'arc',
  'spline',
  'bezier',
  'bspline',
]);

const CURVE_PROFILE_SOURCE_TYPES = new Set<string>([
  'circle_geometry',
  'ellipse_geometry',
  'arc_geometry',
  'spline_geometry',
  'bezier_geometry',
]);

const SPLINE_LIKE_RUNTIME_ENTITY_TYPES = new Set<string>([
  'spline',
  'style_spline',
  'style-spline',
]);

const POINT_MATCH_TOLERANCE = 1e-5;
const POINT_MATCH_TOLERANCE_SQUARED = POINT_MATCH_TOLERANCE * POINT_MATCH_TOLERANCE;
const LOOP_MATCH_RELATIVE_TOLERANCE = 0.01;

type ProfilePoint3 = [number, number, number];

/**
 * 判断输入是否为可安全读取字段的普通对象。
 *
 * @param value 待判断值。
 * @returns 是否为对象记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * 读取三维点。
 *
 * @param value 原始点数据。
 * @returns 标准三维点；无效时返回 null。
 */
function readPoint3(value: unknown): ProfilePoint3 | null {
  if (Array.isArray(value) && value.length >= 3) {
    const [x, y, z] = value;
    if (typeof x === 'number' && Number.isFinite(x)
      && typeof y === 'number' && Number.isFinite(y)
      && typeof z === 'number' && Number.isFinite(z)) {
      return [x, y, z];
    }
  }
  if (isRecord(value)) {
    const {x, y, z} = value;
    if (typeof x === 'number' && Number.isFinite(x)
      && typeof y === 'number' && Number.isFinite(y)
      && typeof z === 'number' && Number.isFinite(z)) {
      return [x, y, z];
    }
  }
  return null;
}

/**
 * 读取三维点列表。
 *
 * @param value 原始点列表。
 * @returns 标准点列表；无效时返回空数组。
 */
function readPointList(value: unknown): ProfilePoint3[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((point) => readPoint3(point))
    .filter((point): point is ProfilePoint3 => point !== null);
}

/**
 * 判断两个三维点是否接近。
 *
 * @param left 左点。
 * @param right 右点。
 * @returns 是否在轮廓匹配容差内。
 */
function arePointsClose(left: ProfilePoint3, right: ProfilePoint3): boolean {
  const dx = left[0] - right[0];
  const dy = left[1] - right[1];
  const dz = left[2] - right[2];
  return dx * dx + dy * dy + dz * dz <= POINT_MATCH_TOLERANCE_SQUARED;
}

/**
 * 去掉闭合点列重复的尾点。
 *
 * @param points 点列。
 * @returns 标准闭合环点列。
 */
function normalizeClosedPointLoop(points: ProfilePoint3[]): ProfilePoint3[] {
  if (points.length >= 2 && arePointsClose(points[0]!, points[points.length - 1]!)) {
    return points.slice(0, -1);
  }
  return [...points];
}

/**
 * 计算点集包围盒对角线。
 *
 * @param points 点集。
 * @returns 包围盒对角线长度。
 */
function computeBoundingDiagonal(points: ProfilePoint3[]): number {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  points.forEach((point) => {
    minX = Math.min(minX, point[0]);
    minY = Math.min(minY, point[1]);
    minZ = Math.min(minZ, point[2]);
    maxX = Math.max(maxX, point[0]);
    maxY = Math.max(maxY, point[1]);
    maxZ = Math.max(maxZ, point[2]);
  });

  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
    return 0;
  }
  return Math.hypot(maxX - minX, maxY - minY, maxZ - minZ);
}

/**
 * 计算闭合样条点列匹配容差。
 *
 * @param left 左点环。
 * @param right 右点环。
 * @returns 匹配容差。
 */
function resolveClosedLoopMatchTolerance(left: ProfilePoint3[], right: ProfilePoint3[]): number {
  const diagonal = Math.max(
    computeBoundingDiagonal(left),
    computeBoundingDiagonal(right),
  );
  return Math.max(POINT_MATCH_TOLERANCE, diagonal * LOOP_MATCH_RELATIVE_TOLERANCE);
}

/**
 * 计算点到线段的距离平方。
 *
 * @param point 点。
 * @param start 线段起点。
 * @param end 线段终点。
 * @returns 距离平方。
 */
function computePointToSegmentDistanceSquared(
  point: ProfilePoint3,
  start: ProfilePoint3,
  end: ProfilePoint3,
): number {
  const segmentX = end[0] - start[0];
  const segmentY = end[1] - start[1];
  const segmentZ = end[2] - start[2];
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY + segmentZ * segmentZ;
  if (segmentLengthSquared <= POINT_MATCH_TOLERANCE_SQUARED) {
    const dx = point[0] - start[0];
    const dy = point[1] - start[1];
    const dz = point[2] - start[2];
    return dx * dx + dy * dy + dz * dz;
  }

  const pointX = point[0] - start[0];
  const pointY = point[1] - start[1];
  const pointZ = point[2] - start[2];
  const ratio = Math.max(
    0,
    Math.min(
      1,
      (pointX * segmentX + pointY * segmentY + pointZ * segmentZ) / segmentLengthSquared,
    ),
  );
  const closestX = start[0] + segmentX * ratio;
  const closestY = start[1] + segmentY * ratio;
  const closestZ = start[2] + segmentZ * ratio;
  const dx = point[0] - closestX;
  const dy = point[1] - closestY;
  const dz = point[2] - closestZ;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * 计算点到闭合折线的最小距离平方。
 *
 * @param point 点。
 * @param loop 闭合折线点环。
 * @returns 最小距离平方。
 */
function computePointToClosedPolylineDistanceSquared(
  point: ProfilePoint3,
  loop: ProfilePoint3[],
): number {
  let minDistanceSquared = Number.POSITIVE_INFINITY;
  for (let index = 0; index < loop.length; index += 1) {
    const start = loop[index]!;
    const end = loop[(index + 1) % loop.length]!;
    minDistanceSquared = Math.min(
      minDistanceSquared,
      computePointToSegmentDistanceSquared(point, start, end),
    );
  }
  return minDistanceSquared;
}

/**
 * 判断点环是否都落在另一条闭合折线附近。
 *
 * @param points 待检查点环。
 * @param targetLoop 目标闭合折线。
 * @param toleranceSquared 容差平方。
 * @returns 是否全部在容差内。
 */
function arePointsCloseToClosedPolyline(
  points: ProfilePoint3[],
  targetLoop: ProfilePoint3[],
  toleranceSquared: number,
): boolean {
  return points.every((point) => (
    computePointToClosedPolylineDistanceSquared(point, targetLoop) <= toleranceSquared
  ));
}

/**
 * 判断两个闭合点环是否为同一条采样曲线。
 *
 * 设计说明：闭合样条轮廓进入 region 后可能被去掉重复尾点，也可能反向存储。
 * 运行时样条快照与 region 外环还可能来自不同采样密度，因此先做严格循环匹配，
 * 再做闭合折线距离匹配。这里只用于识别“fallback 点集是否来自同一条闭合样条”，
 * 不用于几何求解。
 *
 * @param left 左点环。
 * @param right 右点环。
 * @returns 是否等价。
 */
function areClosedPointLoopsEquivalent(left: ProfilePoint3[], right: ProfilePoint3[]): boolean {
  const normalizedLeft = normalizeClosedPointLoop(left);
  const normalizedRight = normalizeClosedPointLoop(right);
  if (normalizedLeft.length < 3 || normalizedRight.length < 3) {
    return false;
  }

  if (normalizedLeft.length === normalizedRight.length) {
    for (let offset = 0; offset < normalizedRight.length; offset += 1) {
      let forwardMatched = true;
      let reverseMatched = true;
      for (let index = 0; index < normalizedLeft.length; index += 1) {
        const forwardPoint = normalizedRight[(offset + index) % normalizedRight.length]!;
        const reversePoint = normalizedRight[(offset - index + normalizedRight.length) % normalizedRight.length]!;
        if (!arePointsClose(normalizedLeft[index]!, forwardPoint)) {
          forwardMatched = false;
        }
        if (!arePointsClose(normalizedLeft[index]!, reversePoint)) {
          reverseMatched = false;
        }
        if (!forwardMatched && !reverseMatched) {
          break;
        }
      }
      if (forwardMatched || reverseMatched) {
        return true;
      }
    }
  }

  const tolerance = resolveClosedLoopMatchTolerance(normalizedLeft, normalizedRight);
  const toleranceSquared = tolerance * tolerance;
  return arePointsCloseToClosedPolyline(normalizedLeft, normalizedRight, toleranceSquared)
    && arePointsCloseToClosedPolyline(normalizedRight, normalizedLeft, toleranceSquared);
}

/**
 * 读取运行时草图实体 ID。
 *
 * @param entity 运行时草图实体。
 * @returns 实体 ID；不存在时返回 null。
 */
export function getRuntimeSketchEntityId(entity: unknown): string | null {
  if (!isRecord(entity) || typeof entity.entityId !== 'string' || entity.entityId.length <= 0) {
    return null;
  }
  return entity.entityId;
}

/**
 * 判断运行时草图实体是否为闭合样条。
 *
 * @param entity 运行时草图实体。
 * @returns 是否为闭合样条或样式样条。
 */
export function isClosedRuntimeSplineLikeEntity(entity: unknown): boolean {
  if (!isRecord(entity) || typeof entity.entityType !== 'string') {
    return false;
  }
  if (!SPLINE_LIKE_RUNTIME_ENTITY_TYPES.has(entity.entityType)) {
    return false;
  }
  const geometry = isRecord(entity.geometry) ? entity.geometry : {};
  return geometry.isClosed === true || geometry.closureKind === 'closed';
}

/**
 * 判断区域点列是否来自指定闭合样条实体。
 *
 * @param regionGeometry 区域几何。
 * @param entity 运行时样条实体。
 * @returns 点列匹配时返回 true。
 */
export function doesProfileRegionGeometryMatchClosedRuntimeSpline(
  regionGeometry: unknown,
  entity: unknown,
): boolean {
  if (!isRecord(regionGeometry) || !isRecord(entity) || !isClosedRuntimeSplineLikeEntity(entity)) {
    return false;
  }
  const geometry = isRecord(entity.geometry) ? entity.geometry : {};
  const regionOuter = readPointList(regionGeometry.outer);
  const explicitCurvePoints = readPointList(geometry.curvePoints);
  const splineCurvePoints = explicitCurvePoints.length > 0
    ? explicitCurvePoints
    : readPointList(geometry.points);
  return areClosedPointLoopsEquivalent(regionOuter, splineCurvePoints);
}

/**
 * 收集闭合样条运行时实体。
 *
 * @param entities 运行时草图实体列表。
 * @returns 闭合样条实体列表。
 */
function collectClosedRuntimeSplineLikeEntities(entities: unknown): unknown[] {
  if (!Array.isArray(entities)) {
    return [];
  }
  return entities.filter((entity) => isClosedRuntimeSplineLikeEntity(entity));
}

/**
 * 收集 region_geometry 参数内的区域几何。
 *
 * @param params 拉伸参数。
 * @returns 区域几何列表。
 */
function collectExtrudeRegionGeometries(params: Record<string, unknown>): unknown[] {
  if (Array.isArray(params.regionGeometries) && params.regionGeometries.length > 0) {
    return params.regionGeometries;
  }
  return isRecord(params.regionGeometry) ? [params.regionGeometry] : [];
}

/**
 * 判断拉伸参数是否选中了运行时闭合样条。
 *
 * @param params 拉伸参数。
 * @returns 是否可判定为闭合样条轮廓。
 */
function hasSelectedClosedRuntimeSplineLikeEntity(params: Record<string, unknown>): boolean {
  const closedSplineEntities = collectClosedRuntimeSplineLikeEntities(params.runtimeSketchEntities);
  if (closedSplineEntities.length <= 0) {
    return false;
  }

  const contourKeys = Array.isArray(params.contourKeys)
    ? params.contourKeys.filter((value): value is string => typeof value === 'string' && value.length > 0)
    : [];
  if (contourKeys.some((contourKey) => closedSplineEntities.some((entity) => getRuntimeSketchEntityId(entity) === contourKey))) {
    return true;
  }

  const regionGeometries = collectExtrudeRegionGeometries(params);
  if (regionGeometries.some((regionGeometry) => (
    closedSplineEntities.some((entity) => doesProfileRegionGeometryMatchClosedRuntimeSpline(regionGeometry, entity))
  ))) {
    return true;
  }

  return contourKeys.length <= 0
    && regionGeometries.length === 1
    && closedSplineEntities.length === 1;
}

/**
 * 判断 profile 曲线段是否属于样条族。
 *
 * @param segment profile 曲线段。
 * @returns 是否为样条、B 样条或贝塞尔曲线段。
 */
export function isSplineLikeProfileSegment(segment: unknown): boolean {
  if (!isRecord(segment)) {
    return false;
  }
  if (segment.kind === 'spline') {
    return true;
  }
  return typeof segment.curveKind === 'string'
    && SPLINE_LIKE_PROFILE_CURVE_KINDS.has(segment.curveKind);
}

/**
 * 判断曲线段集合中是否包含样条族曲线。
 *
 * @param segments 曲线段集合。
 * @returns 是否存在样条族曲线段。
 */
export function hasSplineLikeProfileSegments(segments: unknown): boolean {
  return Array.isArray(segments)
    && segments.some((segment) => isSplineLikeProfileSegment(segment));
}

/**
 * 判断草图区域几何是否包含样条族轮廓。
 *
 * @param regionGeometry 草图区域几何。
 * @returns 是否存在样条族外环或内环。
 */
export function hasSplineLikeProfileRegionGeometry(regionGeometry: unknown): boolean {
  if (!isRecord(regionGeometry)) {
    return false;
  }
  if (hasSplineLikeProfileSegments(regionGeometry.outerSegments)) {
    return true;
  }
  return Array.isArray(regionGeometry.holeSegments)
    && regionGeometry.holeSegments.some((segments) => hasSplineLikeProfileSegments(segments));
}

/**
 * 判断草图区域几何集合是否包含样条族轮廓。
 *
 * @param regionGeometries 草图区域几何集合。
 * @returns 是否存在样条族区域。
 */
export function hasSplineLikeProfileRegionGeometries(regionGeometries: unknown): boolean {
  return Array.isArray(regionGeometries)
    && regionGeometries.some((regionGeometry) => hasSplineLikeProfileRegionGeometry(regionGeometry));
}

/**
 * 判断 profile 曲线段是否属于需要高精度网格化的曲线族。
 *
 * 设计说明：圆、椭圆、圆弧与样条在 Puppy CAD 内核 中都是精确曲线，若继续使用粗交互三角化，
 * 视口会把真实曲面显示成低边数多面体；这里统一从 profile 语义识别曲线，而不是在 UI 层补参数。
 *
 * @param segment profile 曲线段。
 * @returns 是否为曲线 profile 段。
 */
export function isCurveProfileSegment(segment: unknown): boolean {
  if (!isRecord(segment)) {
    return false;
  }
  if (typeof segment.kind === 'string' && CURVE_PROFILE_SEGMENT_KINDS.has(segment.kind)) {
    return true;
  }
  return typeof segment.curveKind === 'string'
    && SPLINE_LIKE_PROFILE_CURVE_KINDS.has(segment.curveKind);
}

/**
 * 判断曲线段集合中是否包含需要高精度网格化的曲线。
 *
 * @param segments 曲线段集合。
 * @returns 是否存在曲线 profile 段。
 */
export function hasCurveProfileSegments(segments: unknown): boolean {
  return Array.isArray(segments)
    && segments.some((segment) => isCurveProfileSegment(segment));
}

/**
 * 判断草图区域几何是否包含曲线轮廓。
 *
 * @param regionGeometry 草图区域几何。
 * @returns 是否存在曲线外环或内环。
 */
export function hasCurveProfileRegionGeometry(regionGeometry: unknown): boolean {
  if (!isRecord(regionGeometry)) {
    return false;
  }
  if (hasCurveProfileSegments(regionGeometry.outerSegments)) {
    return true;
  }
  return Array.isArray(regionGeometry.holeSegments)
    && regionGeometry.holeSegments.some((segments) => hasCurveProfileSegments(segments));
}

/**
 * 判断草图区域几何集合是否包含曲线轮廓。
 *
 * @param regionGeometries 草图区域几何集合。
 * @returns 是否存在曲线区域。
 */
export function hasCurveProfileRegionGeometries(regionGeometries: unknown): boolean {
  return Array.isArray(regionGeometries)
    && regionGeometries.some((regionGeometry) => hasCurveProfileRegionGeometry(regionGeometry));
}

/**
 * 判断特征参数是否来自曲线 profile。
 *
 * @param params 拉伸或旋转参数。
 * @returns 是否需要按曲线 profile 使用高精度网格化。
 */
export function hasCurveProfileParams(params: unknown): boolean {
  if (!isRecord(params)) {
    return false;
  }
  if (typeof params.sourceType === 'string' && CURVE_PROFILE_SOURCE_TYPES.has(params.sourceType)) {
    return true;
  }
  if (params.sourceType !== 'region_geometry') {
    return false;
  }
  return hasCurveProfileRegionGeometry(params.regionGeometry)
    || hasCurveProfileRegionGeometries(params.regionGeometries);
}

/**
 * 判断拉伸参数是否来自样条族轮廓。
 *
 * @param params 拉伸参数或其结构化子集。
 * @returns 是否需要按样条拉伸路径处理。
 */
export function hasSplineLikeExtrudeParams(params: unknown): boolean {
  if (!isRecord(params)) {
    return false;
  }
  if (typeof params.sourceType === 'string' && SPLINE_LIKE_EXTRUDE_SOURCE_TYPES.has(params.sourceType)) {
    return true;
  }
  if (params.sourceType !== 'region_geometry') {
    return false;
  }
  return hasSplineLikeProfileRegionGeometry(params.regionGeometry)
    || hasSplineLikeProfileRegionGeometries(params.regionGeometries)
    || hasSelectedClosedRuntimeSplineLikeEntity(params);
}
